import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiUser, FiArrowRight, FiTrendingUp, FiShield, FiDatabase } = FiIcons;

const BlogPage = () => {
  const featuredPost = {
    id: 1,
    title: 'The Complete Guide to Microsoft Purview Implementation in 2024',
    excerpt: 'Everything you need to know about implementing Microsoft Purview successfully in your organization, including best practices, common pitfalls, and expert tips.',
    author: 'Michael Rodriguez',
    date: '2024-01-15',
    readTime: '12 min read',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop',
    category: 'Implementation',
    slug: 'complete-guide-microsoft-purview-implementation-2024'
  };

  const blogPosts = [
    {
      id: 2,
      title: 'GDPR Compliance Made Easy with Microsoft Purview',
      excerpt: 'Learn how to leverage Microsoft Purview\'s powerful features to achieve and maintain GDPR compliance effortlessly.',
      author: 'Sarah Chen',
      date: '2024-01-10',
      readTime: '8 min read',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&h=250&fit=crop',
      category: 'Compliance',
      slug: 'gdpr-compliance-microsoft-purview'
    },
    {
      id: 3,
      title: 'Data Classification Best Practices for Enterprise Security',
      excerpt: 'Discover proven strategies for classifying sensitive data and implementing automated protection policies.',
      author: 'David Thompson',
      date: '2024-01-08',
      readTime: '10 min read',
      image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=400&h=250&fit=crop',
      category: 'Security',
      slug: 'data-classification-best-practices-enterprise-security'
    },
    {
      id: 4,
      title: '5 Common Microsoft Purview Implementation Mistakes to Avoid',
      excerpt: 'Learn from our experience helping 500+ companies avoid these costly implementation pitfalls.',
      author: 'Michael Rodriguez',
      date: '2024-01-05',
      readTime: '7 min read',
      image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=250&fit=crop',
      category: 'Best Practices',
      slug: 'common-purview-implementation-mistakes-avoid'
    },
    {
      id: 5,
      title: 'ROI Calculator: Measuring Your Microsoft Purview Success',
      excerpt: 'Use our proven framework to calculate and demonstrate the return on investment from your data governance initiatives.',
      author: 'Sarah Chen',
      date: '2024-01-03',
      readTime: '6 min read',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop',
      category: 'ROI',
      slug: 'roi-calculator-microsoft-purview-success'
    },
    {
      id: 6,
      title: 'Microsoft Purview vs Traditional Data Governance Tools',
      excerpt: 'A comprehensive comparison of Microsoft Purview with legacy data governance solutions and why modern companies are making the switch.',
      author: 'David Thompson',
      date: '2024-01-01',
      readTime: '9 min read',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop',
      category: 'Comparison',
      slug: 'microsoft-purview-vs-traditional-data-governance'
    }
  ];

  const categories = [
    { name: 'All Posts', count: 15, icon: FiDatabase },
    { name: 'Implementation', count: 6, icon: FiTrendingUp },
    { name: 'Compliance', count: 4, icon: FiShield },
    { name: 'Security', count: 3, icon: FiShield },
    { name: 'Best Practices', count: 2, icon: FiTrendingUp }
  ];

  return (
    <div className="py-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-purple-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl font-bold mb-6">
                Microsoft Purview Insights & Best Practices
              </h1>
              <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                Stay ahead with expert insights, implementation guides, and proven strategies from our team of Microsoft Purview specialists.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Featured Article</h2>
            <p className="text-lg text-gray-600">Our latest and most comprehensive guide</p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-lg shadow-xl overflow-hidden"
          >
            <div className="md:flex">
              <div className="md:w-1/2">
                <img src={featuredPost.image} alt={featuredPost.title} className="w-full h-64 md:h-full object-cover" />
              </div>
              <div className="md:w-1/2 p-8">
                <div className="flex items-center space-x-4 mb-4">
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                    {featuredPost.category}
                  </span>
                  <span className="text-gray-500 text-sm">{featuredPost.readTime}</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {featuredPost.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <SafeIcon icon={FiUser} className="mr-1" />
                      {featuredPost.author}
                    </div>
                    <div className="flex items-center">
                      <SafeIcon icon={FiCalendar} className="mr-1" />
                      {new Date(featuredPost.date).toLocaleDateString()}
                    </div>
                  </div>
                  <Link
                    to={`/blog/${featuredPost.slug}`}
                    className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    Read More
                    <SafeIcon icon={FiArrowRight} className="ml-2" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Main Content */}
            <div className="lg:w-2/3">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Latest Articles</h2>
              <div className="grid gap-8">
                {blogPosts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-gray-50 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                  >
                    <div className="md:flex">
                      <div className="md:w-1/3">
                        <img src={post.image} alt={post.title} className="w-full h-48 md:h-full object-cover" />
                      </div>
                      <div className="md:w-2/3 p-6">
                        <div className="flex items-center space-x-4 mb-3">
                          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                            {post.category}
                          </span>
                          <span className="text-gray-500 text-sm">{post.readTime}</span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">
                          <Link
                            to={`/blog/${post.slug}`}
                            className="hover:text-blue-600 transition-colors"
                          >
                            {post.title}
                          </Link>
                        </h3>
                        <p className="text-gray-600 mb-4">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <div className="flex items-center">
                              <SafeIcon icon={FiUser} className="mr-1" />
                              {post.author}
                            </div>
                            <div className="flex items-center">
                              <SafeIcon icon={FiCalendar} className="mr-1" />
                              {new Date(post.date).toLocaleDateString()}
                            </div>
                          </div>
                          <Link
                            to={`/blog/${post.slug}`}
                            className="text-blue-600 hover:text-blue-700 font-semibold flex items-center"
                          >
                            Read More
                            <SafeIcon icon={FiArrowRight} className="ml-1" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            </div>
            {/* Sidebar */}
            <div className="lg:w-1/3">
              {/* Categories */}
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Categories</h3>
                <div className="space-y-3">
                  {categories.map((category, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <SafeIcon icon={category.icon} className="text-blue-600" />
                        <span className="text-gray-700 hover:text-blue-600 cursor-pointer">
                          {category.name}
                        </span>
                      </div>
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
                        {category.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Newsletter Signup */}
              <div className="bg-blue-600 text-white p-6 rounded-lg mb-8">
                <h3 className="text-xl font-bold mb-3">Stay Updated</h3>
                <p className="mb-4">Get the latest Microsoft Purview insights delivered to your inbox.</p>
                <div className="space-y-3">
                  <input
                    type="email"
                    placeholder="Your email address"
                    className="w-full px-4 py-2 rounded-lg text-gray-900"
                  />
                  <button className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg font-semibold transition-colors">
                    Subscribe
                  </button>
                </div>
              </div>
              {/* Popular Posts */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Popular Posts</h3>
                <div className="space-y-4">
                  {blogPosts.slice(0, 3).map((post, index) => (
                    <div key={index} className="flex space-x-3">
                      <img src={post.image} alt={post.title} className="w-16 h-16 object-cover rounded" />
                      <div className="flex-1">
                        <Link
                          to={`/blog/${post.slug}`}
                          className="text-sm font-semibold text-gray-900 hover:text-blue-600 line-clamp-2"
                        >
                          {post.title}
                        </Link>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(post.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Implement These Strategies?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Let our experts help you apply these best practices to your Microsoft Purview implementation
          </p>
          <Link
            to="/purview-audit"
            className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all transform hover:scale-105"
          >
            Get Expert Consultation
            <SafeIcon icon={FiArrowRight} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;